import type React from 'react';
import { matchPath } from 'react-router-dom';
import { UserRole, isAtLeast } from '@core/enum/userRole';
import type { NavItem } from '@core/interfaces';

export const DASHBOARD_PATH = '/dashboard';

export interface RouteConfigItem {
  path: string;
  component: React.ComponentType;
}

export interface ResolvedRoute {
  path: string;
  parent: { path: string; title: string } | null;
  childTitle: string | null;
}

export function filterNavItemsByRole(
  navItems: NavItem[],
  role: UserRole
): NavItem[] {
  return navItems.filter(
    (item) => !item.minRole || isAtLeast(role, item.minRole)
  );
}

/**
 * Builds a flat list of routes from nav items (including children),
 * ordered so more specific paths come first for correct React Router matching.
 */
export function getRouteConfig(navItems: NavItem[]): RouteConfigItem[] {
  const config: RouteConfigItem[] = [];
  for (const item of navItems) {
    config.push({ path: item.path, component: item.component });
    for (const child of item.children ?? []) {
      config.push({ path: child.path, component: child.component });
    }
  }
  return config.sort((a, b) => b.path.length - a.path.length);
}

/** First nav item is treated as the app home (supports optional `/*` splat suffix). */
export function getHomePath(navItems: NavItem[]): string {
  const first = navItems[0]?.path ?? DASHBOARD_PATH;
  return first.endsWith('/*') ? first.slice(0, -2) : first;
}

/**
 * Resolves the current route from pathname + navItems. Child routes are matched
 * first, then top-level (path or path + suffix). Used for breadcrumbs.
 */
export function getResolvedRoute(
  pathname: string,
  navItems: NavItem[]
): ResolvedRoute | undefined {
  for (const item of navItems) {
    for (const child of item.children ?? []) {
      if (matchPath({ path: child.path, end: true }, pathname) != null) {
        return {
          path: child.path,
          parent: { path: item.path, title: item.title },
          childTitle: child.title,
        };
      }
    }
  }
  for (const item of navItems) {
    if (matchesNavPath(pathname, item.path)) {
      return { path: item.path, parent: null, childTitle: null };
    }
  }
  return undefined;
}

/** Strips a React Router splat suffix for use in Link/navigate targets. */
export function getNavLinkPath(path: string): string {
  return path.endsWith('/*') ? path.slice(0, -2) : path;
}

/** Checks if a path matches a nav path (supports `/*` splat suffix). */
export function matchesNavPath(pathname: string, path: string): boolean {
  const base = path.endsWith('/*') ? path.slice(0, -2) : path;
  return pathname === base || pathname.startsWith(`${base}/`);
}
